import React from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { Switch } from "./ui/switch";
import { readConsent, saveConsent, applyConsent } from "../lib/analytics";

const btnDark =
  "px-5 py-2.5 rounded-full bg-[#1a1816] text-white text-sm font-medium hover:bg-[#2a251f] transition-colors";
const btnLight =
  "px-5 py-2.5 rounded-full border border-[#1a1816]/15 text-[#1a1816] text-sm font-medium hover:bg-[#f5f1e8] transition-colors";

function PrefRow({ title, text, children }) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-xl border border-[#e8ddc3] bg-[#faf7f0] p-4">
      <div>
        <div className="text-sm font-medium text-[#1a1816]">{title}</div>
        <p className="text-xs text-[#4a4136] mt-1 leading-relaxed">{text}</p>
      </div>
      {children}
    </div>
  );
}

function Preferences({ analytics, setAnalytics }) {
  return (
    <div className="mt-4 space-y-3" data-testid="cookie-preferences-panel">
      <PrefRow
        title="Técnicas (siempre activas)"
        text="Necesarias para que la web funcione y para procesar el pago de forma segura con Stripe."
      >
        <span className="text-xs text-[#8a7340] whitespace-nowrap pt-1">Siempre activas</span>
      </PrefRow>
      <PrefRow
        title="Analítica (Google Analytics 4)"
        text="Nos ayudan a saber cuántas personas visitan la web y qué pasos del pedido funcionan mejor. No enviamos fotos, nombres, correos, teléfonos ni direcciones."
      >
        <Switch
          checked={analytics}
          onCheckedChange={setAnalytics}
          className="data-[state=unchecked]:bg-[#bfb39a] data-[state=checked]:bg-[#b59557]"
          aria-label="Permitir cookies de analítica"
          data-testid="cookie-analytics-toggle"
        />
      </PrefRow>
    </div>
  );
}

export default function CookieBanner() {
  const [visible, setVisible] = React.useState(false);
  const [showPrefs, setShowPrefs] = React.useState(false);
  const [analytics, setAnalytics] = React.useState(false);

  React.useEffect(() => {
    applyConsent();
    const openPrefs = () => {
      setAnalytics(readConsent()?.analytics === true);
      setShowPrefs(true);
      setVisible(true);
    };
    window.addEventListener("rm:open-cookie-prefs", openPrefs);
    let t;
    if (!readConsent()) t = setTimeout(() => setVisible(true), 600);
    return () => {
      clearTimeout(t);
      window.removeEventListener("rm:open-cookie-prefs", openPrefs);
    };
  }, []);

  const decide = (value) => {
    saveConsent({ analytics: value });
    setVisible(false);
    setShowPrefs(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[60] px-4 pb-4 sm:px-6 sm:pb-6 pointer-events-none"
      data-testid="cookie-banner"
    >
      <div className="pointer-events-auto mx-auto max-w-3xl max-h-[85vh] overflow-y-auto rounded-2xl border border-[#e8ddc3] bg-white shadow-2xl shadow-black/10 p-5 md:p-6">
        <div className="flex items-start gap-4">
          <div className="hidden sm:flex h-11 w-11 shrink-0 rounded-full bg-[#efe6cf] items-center justify-center">
            <Cookie className="h-5 w-5 text-[#b59557]" />
          </div>
          <div className="flex-1">
            <div className="flex items-start justify-between gap-3">
              <h3 className="font-serif text-lg text-[#1a1816]">
                {showPrefs ? "Configurar cookies" : "Usamos cookies"}
              </h3>
              <button
                type="button"
                aria-label="Cerrar"
                onClick={() => (readConsent() ? setVisible(false) : decide(false))}
                className="text-[#7a6a48] hover:text-[#1a1816]"
                data-testid="cookie-close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="text-sm text-[#3a3228] leading-relaxed mt-1">
              Usamos cookies técnicas imprescindibles y, solo si lo aceptas, cookies de analítica
              (<strong>Google Analytics 4</strong>) para mejorar la web. No usamos cookies
              publicitarias. Puedes cambiar o retirar tu consentimiento en cualquier momento desde
              &laquo;Configurar cookies&raquo; en el pie de página.{" "}
              <Link
                to="/cookies"
                className="text-[#b59557] underline hover:text-[#1a1816]"
                data-testid="cookie-banner-policy-link"
              >
                Más información
              </Link>
              .
            </p>

            {showPrefs && <Preferences analytics={analytics} setAnalytics={setAnalytics} />}

            <div className="mt-4 flex flex-col sm:flex-row gap-2 sm:gap-3">
              <button type="button" onClick={() => decide(true)} className={btnDark} data-testid="cookie-accept">
                Aceptar todas
              </button>
              <button type="button" onClick={() => decide(false)} className={btnLight} data-testid="cookie-reject">
                Rechazar
              </button>
              {showPrefs ? (
                <button type="button" onClick={() => decide(analytics)} className={btnLight} data-testid="cookie-save-prefs">
                  Guardar preferencias
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setAnalytics(readConsent()?.analytics === true);
                    setShowPrefs(true);
                  }}
                  className={btnLight}
                  data-testid="cookie-configure"
                >
                  Configurar
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
